import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import styles from "./recordMeetingSearch.module.css";
import { tWithKeyAttr as t } from "../../i18nHelper";
import { useAppSelector } from "../../services/hooks";
import { therapistUsersSelector, therapistIdSelector, userSelector } from "../../services/selectors/user.selectors";
import { useLazyGetUsersQuery, useLazyGetPendingRecordingSessionsQuery } from "../../services/api/user.api";
import { IUser } from "../../types/types";
import { filterObjectsByString } from "../../utils/search";
import BackArrowButton from "../backArrowButton/backArrowButton";
import i18n from "../../i18n";
import Modal from "../modals/modal/Modal";
import DeleteConfirmation from "../modals/deleteConfirmation/deleteConfirmation";
import PendingRecordingPopup from "../modals/pendingRecordingPopup/pendingRecordingPopup";
import { isSessionLive, getActiveRecording } from "../../utils/logoutLock";
import AppointmentPicker from "../appointmentPicker/appointmentPicker";

const RECENT_PATIENTS_KEY = "recentPatients";
const MAX_RECENT_PATIENTS = 5;

interface RecentPatientEntry {
  guid: string;
  timestamp: number;
}

const getRecentPatientGuids = (): string[] => {
  try {
    const stored = localStorage.getItem(RECENT_PATIENTS_KEY);
    if (!stored) return [];
    const entries: RecentPatientEntry[] = JSON.parse(stored);
    return entries
      .sort((a, b) => b.timestamp - a.timestamp)
      .map((e) => e.guid);
  } catch {
    return [];
  }
};

const addRecentPatient = (guid: string): void => {
  try {
    const stored = localStorage.getItem(RECENT_PATIENTS_KEY);
    let entries: RecentPatientEntry[] = stored ? JSON.parse(stored) : [];
    entries = entries.filter((e) => e.guid !== guid);
    entries.unshift({ guid, timestamp: Date.now() });
    entries = entries.slice(0, MAX_RECENT_PATIENTS);
    localStorage.setItem(RECENT_PATIENTS_KEY, JSON.stringify(entries));
  } catch {
    // Ignore storage errors
  }
};

const RecordMeetingSearch: React.FC = () => {
  const navigate = useNavigate();
  const therapistUsers = useAppSelector(therapistUsersSelector);
  const therapistId = useAppSelector(therapistIdSelector);
  const therapist = useAppSelector(userSelector);
  const [getAllTherapistUsers, { isLoading }] = useLazyGetUsersQuery();
  const [triggerGetPendingSessions] = useLazyGetPendingRecordingSessionsQuery();

  const [searchTerm, setSearchTerm] = useState<string>("");
  const [selectedPatient, setSelectedPatient] = useState<IUser | null>(null);
  const [recentGuids, setRecentGuids] = useState<string[]>([]);
  const [pendingSessions, setPendingSessions] = useState<any[]>([]);
  const [showPendingModal, setShowPendingModal] = useState(false);
  const [checkingPending, setCheckingPending] = useState(false);
  const [showInactiveModal, setShowInactiveModal] = useState(false);
  const [showOtherTabRecordingModal, setShowOtherTabRecordingModal] = useState<string | null>(null);

  // Appointment linking state
  const [showAppointmentPicker, setShowAppointmentPicker] = useState(false);

  const isRtl = i18n.dir() === "rtl";

  const isPatientInactive = (patient: IUser) => patient.StatusId !== undefined && patient.StatusId !== 1;
  const inactiveStyle = { opacity: 0.5, filter: "grayscale(0.4)" };

  useEffect(() => {
    getAllTherapistUsers();
    setRecentGuids(getRecentPatientGuids());
  }, [getAllTherapistUsers]);

  // Get recent patients from therapistUsers based on stored guids
  const recentPatients = useMemo((): IUser[] => {
    if (!therapistUsers || therapistUsers.length === 0 || recentGuids.length === 0) {
      return [];
    }
    const patients: IUser[] = [];
    for (const guid of recentGuids) {
      const found = therapistUsers.find((u: IUser) => u.Guid === guid);
      if (found) patients.push(found);
    }
    return patients;
  }, [therapistUsers, recentGuids]);

  // Filter patients based on search term
  const filteredPatients = useMemo((): IUser[] => {
    if (!searchTerm.trim()) {
      return [];
    }
    return filterObjectsByString(searchTerm, therapistUsers) as IUser[];
  }, [searchTerm, therapistUsers]);

  const handlePatientSelect = (patient: IUser) => {
    setSelectedPatient(patient);
  };

  const handleViewRecordings = (e: React.MouseEvent, patient: IUser) => {
    e.stopPropagation();
    navigate("/users/recordTable", {
      state: {
        Guid: patient.Guid,
        UsersId: patient.Id,
        patientName: `${patient.FirstName || ""} ${patient.LastName || ""}`.trim(),
        patientPassport: patient.Passport || "",
      },
    });
  };

  const navigateToRecording = (patient: IUser, appointmentId?: number | null, appointmentStartTime?: string) => {
    if (patient.Guid) {
      addRecentPatient(patient.Guid);
    }
    navigate("/users/recording", {
      state: {
        Guid: patient.Guid,
        UsersId: patient.Id,
        patientName: `${patient.FirstName || ""} ${patient.LastName || ""}`.trim(),
        patientPassport: patient.Passport || "",
        StatusId: patient.StatusId,
        appointmentId: appointmentId || undefined,
        appointmentStartTime: appointmentStartTime || undefined,
      },
    });
  };

  const handleContinue = async () => {
    if (!selectedPatient || !therapistId) return;

    // Block if another tab already has an active recording
    const activeRec = getActiveRecording();
    if (activeRec) {
      setShowOtherTabRecordingModal(
        `${t("RECORDING_ACTIVE_IN_OTHER_TAB")} (${activeRec.patientName})`
      );
      return;
    }

    // Check for pending recording sessions for the selected patient
    setCheckingPending(true);
    try {
      const result = await triggerGetPendingSessions(Number(therapistId)).unwrap();
      if (result?.success && result.sessions?.length > 0) {
        const patientSessions = result.sessions.filter(
          (s: any) => s.userId === selectedPatient.Id && !isSessionLive(s.sessionId)
        );
        if (patientSessions.length > 0) {
          setPendingSessions(patientSessions);
          setShowPendingModal(true);
          setCheckingPending(false);
          return;
        }
      }
    } catch {
      // If check fails, proceed to recording anyway
    }
    setCheckingPending(false);

    // Show appointment picker so therapist can optionally link to an appointment
    setShowAppointmentPicker(true);
  };

  const maskId = (id: string | undefined | null): string => {
    if (!id) return "*****";
    if (id.length <= 4) return id;
    return "*****" + id.slice(-4);
  };

  return (
    <div className={styles.container} dir={i18n.dir()}>
      {/* Compact Header */}
      <div className={styles.header}>
        <div className={styles.backButton}>
          <BackArrowButton
            text={t("BACK_BUTTON")}
            onClick={() => navigate(-1)}
            position="center"
          />
        </div>
        <div className={styles.headerContent}>
          <h1 className={styles.headerTitle}>
            🎙️ {t("RECORD_MEETING_TITLE")}
          </h1>
          {/* Step Indicator - Inline */}
          <div className={styles.stepIndicator}>
            <div className={`${styles.step} ${styles.active}`}>
              <span className={styles.stepCircle}>1</span>
              <span className={styles.stepLabel}>{t("RECORD_MEETING_STEP_SELECT_PATIENT")}</span>
            </div>
            <div className={styles.stepDivider}>→</div>
            <div className={styles.step}>
              <span className={styles.stepCircle}>2</span>
              <span className={styles.stepLabel}>{t("RECORD_MEETING_STEP_RECORD")}</span>
            </div>
            <div className={styles.stepDivider}>→</div>
            <div className={styles.step}>
              <span className={styles.stepCircle}>3</span>
              <span className={styles.stepLabel}>{t("RECORD_MEETING_STEP_SUMMARY")}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className={styles.content}>
        {/* Search Box with inline button */}
        <div className={styles.searchWrapper}>
          <div className={styles.searchFieldWrapper}>
            <label className={styles.searchLabel}>{t("RECORD_MEETING_SEARCH_PATIENT")}</label>
            <div className={`${styles.searchBox} ${isRtl ? styles.searchBoxRtl : ""}`}>
              <input
                type="text"
                className={styles.searchInput}
                placeholder={t("RECORD_MEETING_SEARCH_PLACEHOLDER")}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                autoComplete="off"
                autoFocus
              />
              <span className={styles.searchInputIcon}>🔍</span>
            </div>
          </div>
          <div className={styles.buttonWrapper}>
            <button
              className={`${styles.continueBtn} ${selectedPatient ? styles.visible : ""}`}
              onClick={handleContinue}
              disabled={!selectedPatient || checkingPending}
            >
              {checkingPending ? "..." : t("RECORD_MEETING_CONTINUE")}
            </button>
          </div>
        </div>

        {/* Recent Patients - shown when not searching */}
        {!searchTerm.trim() && recentPatients.length > 0 && (
          <div className={styles.recentSection}>
            <div className={styles.recentHeader}>{t("RECENT_PATIENTS") || "מטופלים אחרונים"}</div>
            <div className={styles.resultsArea}>
              <div className={styles.patientsList}>
                {recentPatients.map((patient) => (
                  <div
                    key={patient.Guid}
                    className={`${styles.patientItem} ${isRtl ? styles.patientItemRtl : ""} ${
                      selectedPatient?.Guid === patient.Guid ? styles.selected : ""
                    }`}
                    style={isPatientInactive(patient) ? inactiveStyle : undefined}
                    onClick={() => {
                      if (isPatientInactive(patient)) {
                        setShowInactiveModal(true);
                        return;
                      }
                      handlePatientSelect(patient);
                    }}
                  >
                    <div className={styles.patientAvatar}>
                      {patient.FirstName?.charAt(0)?.toUpperCase() || "👤"}
                    </div>
                    <div className={styles.patientInfo}>
                      <div className={styles.patientName}>
                        {patient.FirstName} {patient.LastName || ""}
                      </div>
                      <div className={styles.patientDetails}>
                        {t("ID_NUMBER")}: {maskId(patient.Passport)} • {patient.Phone || "-"}
                      </div>
                    </div>
                    <button
                      className={styles.viewRecordingsBtn}
                      onClick={(e) => handleViewRecordings(e, patient)}
                      title={t("VIEW_RECORDINGS") || "View Recordings"}
                    >
                      📋
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Empty State - shown when no recent patients and not searching */}
        {!searchTerm.trim() && recentPatients.length === 0 && !isLoading && (
          <div className={styles.emptyStateVisible}>
            <div className={styles.emptyIcon}>👤</div>
            <p className={styles.emptyText}>{t("RECORD_MEETING_SEARCH_HINT") || "הקלד שם מטופל בשדה החיפוש"}</p>
          </div>
        )}

        {/* Results Area - only shown when searching */}
        {(isLoading || (searchTerm.trim() && filteredPatients.length > 0) || (searchTerm.trim() && filteredPatients.length === 0)) && (
          <div className={styles.resultsArea}>
            {/* Loading State */}
            {isLoading && (
              <div className={styles.loading}>
                <div className={styles.loadingSpinner}></div>
              </div>
            )}

            {/* Patients List */}
            {!isLoading && searchTerm.trim() && filteredPatients.length > 0 && (
              <div className={styles.patientsList}>
                {filteredPatients.map((patient) => (
                  <div
                    key={patient.Guid}
                    className={`${styles.patientItem} ${isRtl ? styles.patientItemRtl : ""} ${
                      selectedPatient?.Guid === patient.Guid ? styles.selected : ""
                    }`}
                    style={isPatientInactive(patient) ? inactiveStyle : undefined}
                    onClick={() => {
                      if (isPatientInactive(patient)) {
                        setShowInactiveModal(true);
                        return;
                      }
                      handlePatientSelect(patient);
                    }}
                  >
                    <div className={styles.patientAvatar}>
                      {patient.FirstName?.charAt(0)?.toUpperCase() || "👤"}
                    </div>
                    <div className={styles.patientInfo}>
                      <div className={styles.patientName}>
                        {patient.FirstName} {patient.LastName || ""}
                      </div>
                      <div className={styles.patientDetails}>
                        {t("ID_NUMBER")}: {maskId(patient.Passport)} • {patient.Phone || "-"}
                      </div>
                    </div>
                    <button
                      className={styles.viewRecordingsBtn}
                      onClick={(e) => handleViewRecordings(e, patient)}
                      title={t("VIEW_RECORDINGS") || "View Recordings"}
                    >
                      📋
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* No Results */}
            {!isLoading && searchTerm.trim() && filteredPatients.length === 0 && (
              <div className={styles.noResults}>
                <div className={styles.noResultsIcon}>🔍</div>
                <p className={styles.noResultsText}>{t("RECORD_MEETING_NO_RESULTS")}</p>
                <p className={styles.noResultsHint}>{t("RECORD_MEETING_TRY_AGAIN")}</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Another Tab Active Recording Modal */}
      {showOtherTabRecordingModal && (
        <Modal onClose={() => setShowOtherTabRecordingModal(null)}>
          <div style={{ padding: "30px", textAlign: "center", maxWidth: "400px" }}>
            <p style={{ fontSize: "1.2rem", marginBottom: "24px" }}>
              {showOtherTabRecordingModal}
            </p>
            <button
              className="button hover"
              style={{ width: "180px" }}
              onClick={() => setShowOtherTabRecordingModal(null)}
            >
              {t("UNDERSTOOD_BUTTON") || "הבנתי"}
            </button>
          </div>
        </Modal>
      )}

      {/* Inactive Patient Modal */}
      {showInactiveModal && (
        <Modal onClose={() => setShowInactiveModal(false)}>
          <DeleteConfirmation
            hideCloseButton
            text={t("ERROR_PATIENT_INACTIVE")}
            onDelete={async () => {
              setShowInactiveModal(false);
            }}
            onClose={() => setShowInactiveModal(false)}
          />
        </Modal>
      )}

      {/* Appointment Linking Picker Modal */}
      {showAppointmentPicker && selectedPatient && (
        <Modal onClose={() => {
          setShowAppointmentPicker(false);
          navigateToRecording(selectedPatient);
        }}>
          <AppointmentPicker
            patientId={Number(selectedPatient.Id)}
            patientName={`${selectedPatient.FirstName || ""} ${selectedPatient.LastName || ""}`.trim()}
            onSelect={(appointmentId, startTime) => {
              setShowAppointmentPicker(false);
              navigateToRecording(selectedPatient, appointmentId, startTime);
            }}
            onSkip={() => {
              setShowAppointmentPicker(false);
              navigateToRecording(selectedPatient);
            }}
          />
        </Modal>
      )}

      {/* Pending Recording Recovery Modal */}
      {showPendingModal && pendingSessions.length > 0 && (
        <Modal onClose={() => {
          setShowPendingModal(false);
        }}>
          <PendingRecordingPopup
            sessions={pendingSessions}
            onClose={() => {
              setShowPendingModal(false);
            }}
            onDismissed={(sessionId) => {
              const remaining = pendingSessions.filter((s: any) => s.sessionId !== sessionId);
              setPendingSessions(remaining);
              if (remaining.length === 0) {
                setShowPendingModal(false);
                if (selectedPatient) navigateToRecording(selectedPatient);
              }
            }}
            onFinalized={(sessionId, userId, recordNumber, patientGuid, patientName) => {
              const remaining = pendingSessions.filter((s: any) => s.sessionId !== sessionId);
              setPendingSessions(remaining);
              setShowPendingModal(false);
              navigate(`/users/recordTable`, {
                state: {
                  Guid: patientGuid,
                  UsersId: userId,
                  patientName: patientName,
                  newRecordNumber: recordNumber,
                },
              });
            }}
            onContinueRecording={(session) => {
              setShowPendingModal(false);
              navigate("/users/recording", {
                state: {
                  Guid: session.patientGuid,
                  UsersId: session.userId,
                  patientName: session.patientName,
                  appointmentId: session.appointmentId || undefined,
                  resumeSession: {
                    sessionId: session.sessionId,
                    chunkCount: session.chunkCount,
                    mimeType: session.mimeType,
                    durationSeconds: session.recordingDurationSeconds || 0,
                  },
                },
              });
            }}
          />
        </Modal>
      )}
    </div>
  );
};

export default RecordMeetingSearch;
